import {useContext, useRef, useState} from 'react';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import MainCard from 'components/MainCard';
import IconButton from '@mui/material/IconButton';
import Transitions from 'components/@extended/Transitions';
import {NotificationContext} from "@/contexts/NotificationProvider";
import { circularIconHoverSx } from "@/styles/iconHover";

// assets
import BellOutlined from '@ant-design/icons/BellOutlined';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import GiftOutlined from '@ant-design/icons/GiftOutlined';
import MessageOutlined from '@ant-design/icons/MessageOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';

// sx styles
const avatarSX = {
  width: 36,
  height: 36,
  fontSize: '1rem'
};

const actionSX = {
  mt: '6px',
  ml: 1,
  top: 'auto',
  right: 'auto',
  alignSelf: 'flex-start',

  transform: 'none'
};

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

{/*const notifications = [
  {
    time: "3:00 AM",
    read: true,
    icon: <GiftOutlined />,
    iconColor: "success.main",
    iconBg: "success.lighter",
    primary: (
      <Typography variant="h6">
        It&apos;s{" "}
        <Typography component="span" variant="subtitle1">
          Cristina danny&apos;s
        </Typography>{" "}
        birthday today.
      </Typography>
    ),
    secondary: "2 min ago"
  },
  {
    time: "6:00 AM",
    read: false,
    icon: <MessageOutlined />,
    iconColor: "primary.main",
    iconBg: "primary.lighter",
    primary: (
      <Typography variant="h6">
        <Typography component="span" variant="subtitle1">
          Aida Burg
        </Typography>{" "}
        commented your post.
      </Typography>
    ),
    secondary: "5 August"
  },
  {
    time: "2:45 PM",
    read: true,
    icon: <SettingOutlined />,
    iconColor: "error.main",
    iconBg: "error.lighter",
    primary: (
      <Typography variant="h6">
        Your Profile is Complete &nbsp;
        <Typography component="span" variant="subtitle1">
          60%
        </Typography>
      </Typography>
    ),
    secondary: "7 hours ago"
  },
  {
    time: "9:10 PM",
    read: false,
    icon: "C",
    iconColor: "primary.main",
    iconBg: "primary.lighter",
    primary: (
      <Typography variant="h6">
        <Typography component="span" variant="subtitle1">
          Cristina Danny
        </Typography>{" "}
        invited to join{" "}
        <Typography component="span" variant="subtitle1">
          Meeting.
        </Typography>
      </Typography>
    ),
    secondary: "Daily scrum meeting time"
  }
];*/}

export default function Notification() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const { notifications, deleteNotification, deleteNotifications } = useContext(NotificationContext);

  const anchorRef = useRef(null);
  const [read, setRead] = useState(notifications.length || 0);
  const [open, setOpen] = useState(false);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        sx={circularIconHoverSx({
          size: 36,
          color: 'text.primary',
          hoverBg: 'action.hover',
          active: open,
          activeBg: 'action.selected',
        })}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={notifications.length} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>
      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: '100%', minWidth: 285, maxWidth: { xs: 285, md: 420 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="Notification"
                  elevation={0}
                  border={false}
                  content={false}
                  sx={{
                      '& .MuiCardHeader-root': {
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                          p: 2
                      },
                      '& .MuiCardHeader-content': {
                          flex: '1 1 auto'
                      },
                      '& .MuiCardHeader-action': {
                          margin: '0 !important',
                          alignSelf: 'center',
                          display: 'flex'
                      }
                  }}
                  secondary={
                    <>
                      {notifications.length > 0 && (
                        <Tooltip title="Mark as all read">
                          <IconButton color="success" size="small" onClick={() => deleteNotifications()}>
                            <MarkEmailReadRoundedIcon style={{ fontSize: '1.15rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  }
                >
                  <List
                    component="nav"
                    useNativeScrolling={true}
                    showScrollbar="always"
                    sx={{
                      p: 0,
                      '& .MuiListItemButton-root': {
                        py: 0.5,
                        px: 2,
                        '&.Mui-selected': { bgcolor: 'grey.50', color: 'text.primary' },
                        '& .MuiAvatar-root': avatarSX,
                        '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
                      },
                        maxHeight: 100, overflowY: 'auto'
                    }}
                  >
                    {notifications.map((item, idx) => (
                      <ListItem
                        key={idx}
                        component={ListItemButton}
                        divider
                        selected={item}
                        onClick={() => deleteNotification(item)}
                      >
                          <Box sx={{display: 'flex',flexDirection: 'column',width: '100%'}}>
                              <Typography variant="h6" sx={{lineHeight: 1, mb: 0}}>{item.name}</Typography>
                              <Typography variant="caption" sx={{alignSelf: 'flex-end', lineHeight: 1, mt: 0}}>{item.date}</Typography>
                          </Box>
                      </ListItem>
                    ))}
                      {/*
                    <ListItemButton sx={{ textAlign: 'center', py: `${12}px !important` }}>
                      <ListItemText
                        primary={
                          <Typography variant="h6" color="primary">
                            View All
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  */}
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
